import { useState, useEffect } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { EyeIcon, EyeOffIcon, ArrowRightIcon } from "lucide-react";
import { ButtonSignupGoogle } from "@component/button-signup-google";
import { cn } from "@util/tailwind";
import { TokenResponse } from "@react-oauth/google";
import { blackListDomain } from "@constant/domina";

interface EmployerRegisterFormProps {
    onSubmit: (data: CreateAccountData) => void;
    className?: string;
    initialData?: Partial<CreateAccountData>;
    onGoogleSignup: (tokenResponse: TokenResponse) => void;
    isAuthGoogleLoading: boolean
    isLoading: boolean;
    errorMessage: string
}

export interface CreateAccountData {
    firstName: string;
    lastName: string;
    email: string;
    password?: string;
    confirmPassword?: string;
    sub?: string
}

export default function EmployerRegisterForm({
    onSubmit,
    errorMessage,
    className,
    initialData = {},
    onGoogleSignup,
    isLoading = false,
    isAuthGoogleLoading = false
}: EmployerRegisterFormProps) {
    const [formData, setFormData] = useState<CreateAccountData>({
        firstName: initialData.firstName || "",
        lastName: initialData.lastName || "",
        email: initialData.email || "",
        password: initialData.password || "",
        confirmPassword: initialData.confirmPassword || "",
    });

    // Update form data when initialData changes
    useEffect(() => {
        setFormData({
            firstName: initialData.firstName || formData.firstName,
            lastName: initialData.lastName || formData.lastName,
            email: initialData.email || formData.email,
            password: initialData.password || formData.password,
            confirmPassword: initialData.confirmPassword || formData.confirmPassword,
        });
    }, [initialData]);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const toggleShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if ((formData.password || "").length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        setError(null);
        onSubmit(formData);
    };

    const emailFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const email = e.target.value;
        if (email.length > 0) {
            const isBlacklisted = blackListDomain.some((domain) =>
                email.toLowerCase().endsWith(domain)
            );
            const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
            if (isBlacklisted) {
                setError("Please use your work email (must end with @company.com)");
                return
            } else if (!isValidEmail) {
                setError("Please enter a valid email address");
                return
            }
        }
    }

    const isFormComplete =
        formData.firstName &&
        formData.lastName &&
        formData.email &&
        formData.password &&
        formData.password.length >= 8 &&
        formData.confirmPassword === formData.password;

    return (
        <form
            onSubmit={handleSubmit}
            className={cn("space-y-6", className)}
            data-pol-id="d9sqx0"
            data-pol-file-name="create-account-form"
            data-pol-file-type="component"
        >
            <div
                className="space-y-4"
                data-pol-id="hv896b"
                data-pol-file-name="create-account-form"
                data-pol-file-type="component"
            >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div
                        className="space-y-2"
                        data-pol-id="vek3yl"
                        data-pol-file-name="create-account-form"
                        data-pol-file-type="component"
                    >
                        <Label
                            htmlFor="firstName"
                            data-pol-id="fa8ma4"
                            data-pol-file-name="create-account-form"
                            data-pol-file-type="component"
                        >
                            First Name
                        </Label>
                        <Input
                            name="firstName"
                            placeholder="Enter your first name"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                            data-pol-id="mnbczg"
                            data-pol-file-name="create-account-form"
                            data-pol-file-type="component"
                        />
                    </div>

                    <div
                        className="space-y-2"
                        data-pol-id="vek3yl-last"
                        data-pol-file-name="create-account-form"
                        data-pol-file-type="component"
                    >
                        <Label
                            htmlFor="lastName"
                            data-pol-id="fa8ma4-last"
                            data-pol-file-name="create-account-form"
                            data-pol-file-type="component"
                        >
                            Last Name
                        </Label>
                        <Input
                            name="lastName"
                            placeholder="Enter your last name"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                            data-pol-id="mnbczg-last"
                            data-pol-file-name="create-account-form"
                            data-pol-file-type="component"
                        />
                    </div>
                </div>

                <div
                    className="space-y-2"
                    data-pol-id="lu1x9i"
                    data-pol-file-name="create-account-form"
                    data-pol-file-type="component"
                >
                    <Label
                        htmlFor="email"
                        data-pol-id="q7jfwd"
                        data-pol-file-name="create-account-form"
                        data-pol-file-type="component"
                    >
                        Work Email
                    </Label>
                    <Input
                        name="email"
                        type="email"
                        placeholder="name@company.com"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={emailFieldChange}
                        required
                    />
                </div>

                <div
                    className="space-y-2"
                    data-pol-id="sla3jh"
                    data-pol-file-name="create-account-form"
                    data-pol-file-type="component"
                >
                    <Label
                        htmlFor="password"
                        data-pol-id="5x5hp1"
                        data-pol-file-name="create-account-form"
                        data-pol-file-type="component"
                    >
                        Password
                    </Label>
                    <div className="relative">
                        <Input
                            name="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Create a password (min. 8 characters)"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            className="pr-10"
                            data-pol-id="o753cf"
                            data-pol-file-name="create-account-form"
                            data-pol-file-type="component"
                        />

                        <button
                            type="button"
                            onClick={toggleShowPassword}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                            {showPassword ? (
                                <EyeOffIcon className="h-4 w-4" />
                            ) : (
                                <EyeIcon className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    {formData.password && formData.password.length < 8 && (
                        <p
                            className="text-xs text-red-500"
                            data-pol-id="fh7kw5"
                            data-pol-file-name="create-account-form"
                            data-pol-file-type="component"
                        >
                            Password must be at least 8 characters
                        </p>
                    )}
                </div>

                <div
                    className="space-y-2"
                    data-pol-id="z8upf5"
                    data-pol-file-name="create-account-form"
                    data-pol-file-type="component"
                >
                    <Label
                        htmlFor="confirmPassword"
                        data-pol-id="giehft"
                        data-pol-file-name="create-account-form"
                        data-pol-file-type="component"
                    >
                        Confirm Password
                    </Label>
                    <div
                        className="relative"
                        data-pol-id="69jaed"
                        data-pol-file-name="create-account-form"
                        data-pol-file-type="component"
                    >
                        <Input
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm your password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                            className="pr-10"
                            data-pol-id="ifygp2"
                            data-pol-file-name="create-account-form"
                            data-pol-file-type="component"
                        />

                        <button
                            type="button"
                            onClick={toggleShowConfirmPassword}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            data-pol-id="xxylul"
                            data-pol-file-name="create-account-form"
                            data-pol-file-type="component"
                        >
                            {showConfirmPassword ? (
                                <EyeOffIcon
                                    className="h-4 w-4"
                                    data-pol-id="vvympz"
                                    data-pol-file-name="create-account-form"
                                    data-pol-file-type="component"
                                />
                            ) : (
                                <EyeIcon
                                    className="h-4 w-4"
                                    data-pol-id="pykm83"
                                    data-pol-file-name="create-account-form"
                                    data-pol-file-type="component"
                                />
                            )}
                        </button>
                    </div>
                    {formData.confirmPassword &&
                        formData.password !== formData.confirmPassword && (
                            <p
                                className="text-xs text-red-500"
                                data-pol-id="92e5pv"
                                data-pol-file-name="create-account-form"
                                data-pol-file-type="component"
                            >
                                Passwords do not match
                            </p>
                        )}
                </div>
            </div>

            {(error || errorMessage) && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                    {error || errorMessage}
                </div>
            )}

            <Button
                type="submit"
                className="w-full bg-blue-700 hover:bg-blue-800 text-white"
                disabled={!isFormComplete || isLoading}
            >
                {isLoading ? (
                    <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-1" />
                        Please wait...
                    </>
                ) : (
                    <>
                        Continue
                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                    </>
                )}
            </Button>

            <div
                className="relative my-6"
                data-pol-id="b69u5o"
                data-pol-file-name="create-account-form"
                data-pol-file-type="component"
            >
                <div
                    className="absolute inset-0 flex items-center"
                    data-pol-id="t86dy4"
                    data-pol-file-name="create-account-form"
                    data-pol-file-type="component"
                >
                    <span
                        className="w-full border-t border-gray-300"
                        data-pol-id="i56zld"
                        data-pol-file-name="create-account-form"
                        data-pol-file-type="component"
                    ></span>
                </div>
                <div
                    className="relative flex justify-center text-sm"
                    data-pol-id="2m62bj"
                    data-pol-file-name="create-account-form"
                    data-pol-file-type="component"
                >
                    <span
                        className="bg-white px-2 text-gray-500"
                        data-pol-id="4n7xkj"
                        data-pol-file-name="create-account-form"
                        data-pol-file-type="component"
                    >
                        Or continue with
                    </span>
                </div>
            </div>

            <ButtonSignupGoogle
                label="Sign up with Google"
                isLoading={isAuthGoogleLoading}
                onClick={(tokenResponse) => onGoogleSignup(tokenResponse)}
            />

            <p className="mt-4 text-center text-xs text-black-500">
                By creating an account, you agree to our{" "}
                <a
                    href="https://ourteam.ai/terms-of-service"
                    className="text-blue-700 hover:underline"
                    target="_blank"
                >
                    Terms of Service
                </a>{" "}
                and{" "}
                <a
                    href="https://ourteam.ai/privacy-policy"
                    className="text-blue-700 hover:underline"
                    target="_blank"
                >
                    Privacy Policy.
                </a>
            </p>
        </form >
    );
}
